import type { RoomSearchFormProps } from './room-search-form.js';

import { useId } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { Button } from '#ui/button.js';
import { useResizeObserver } from '#ui/hooks/useResizeObserver.js';

import { GuestPicker, StayCalendar } from './room-search-fields.js';
import { FORM_SURFACE, TWO_MONTHS_MIN_WIDTH, useChildAgeSlots } from './room-search-form.js';

/**
 * The room search form opened out, for an empty Rooms tab (ELARA-1092): with nothing else on the tab, the
 * pickers the bar keeps in popovers are on screen — the calendar in a card headed "When", the guests in
 * one headed "Who" — and Find rooms below them. The first search cross-fades it into the bar above the results
 * (`RoomSearchBar`).
 */
export function RoomSearchPanels({
  startDate,
  endDate,
  occupancy,
  prefillKey,
  onDatesChange,
  onOccupancyChange,
  onSearch,
  canSearch,
  isSearching,
  disabled,
}: RoomSearchFormProps) {
  const slots = useChildAgeSlots(occupancy, prefillKey, onOccupancyChange);
  const [formRef, { width }] = useResizeObserver<HTMLDivElement>();

  return (
    <div ref={formRef} className="flex w-full max-w-xl flex-col gap-3">
      {/* A fieldset, so a disabled form (the pre-fill loading, a search running) locks every day and stepper. */}
      <fieldset disabled={disabled} className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
        <FormPanel title="When">
          <StayCalendar
            // Remounted by a fresh pre-fill: the calendar reads the month to show only when it mounts, and
            // the form mounts before the pre-fill lands, so it would stay on this month instead of the stay's.
            key={prefillKey}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDatesChange}
            monthCount={width >= TWO_MONTHS_MIN_WIDTH ? 2 : 1}
          />
        </FormPanel>
        <FormPanel title="Who">
          <GuestPicker occupancy={occupancy} slots={slots} />
        </FormPanel>
      </fieldset>
      {/* On its own and labelled, its words its accessible name: the one thing to do on an empty tab. */}
      <Button onClick={onSearch} disabled={!canSearch} isBusy={isSearching} className="h-12.5 w-full">
        Find rooms
      </Button>
    </div>
  );
}

/**
 * One picker on its own surface, headed inside it by what it sets, which names the section too. The rule
 * under the heading starts under the text and runs out through the surface's right padding to its edge
 * (`-mr-4` with `pr-4`), as the guests picker's row rules do.
 */
function FormPanel({ title, children }: { title: string; children: React.ReactNode }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className={cn(FORM_SURFACE, 'p-4')}>
      <h3
        id={headingId}
        className="m-0 -mr-4 mb-4 border-b pr-4 pb-3 text-xs tracking-wide font-medium text-muted-foreground"
      >
        {title}
      </h3>
      {children}
    </section>
  );
}
